// The process map viewport: the toolbar above it, the SVG itself, and the pan/zoom that moves the
// graph inside it. Everything here is about *looking* at a laid-out graph — which instances are in
// scope and what is selected belong to useProcessGraph, handed in as `state`.
import * as Popover from "@radix-ui/react-popover";
import * as Tooltip from "@radix-ui/react-tooltip";
import {
  ChevronDown,
  ChevronUp,
  CircleQuestionMark,
  LoaderCircle,
  Maximize2,
  Minimize2,
  Minus,
  Plus,
  Scan,
  Search,
  SlidersHorizontal,
  TriangleAlert,
  X,
} from "lucide-react";
// biome-ignore lint/style/useImportType: Classic JSX runtime requires React.
import React from "react";
import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import { formatDuration } from "../../utils";
import { AppSelect } from "../AppSelect";
import type { UserFilterOption } from "../UserFilterMultiSelect";
import ProcessFilters from "./ProcessFilters";
import ProcessGraphCaseControl from "./ProcessGraphCaseControl";
import { appIconUri } from "./appIcon";
import { stepApps } from "./processAppUsage";
import {
  type CompareSide,
  compareNodeSides,
  compareNodeStats,
  compareSideColor,
  compareSideFill,
  compareSideIds,
  formatCompareCount,
} from "./processCompareModel";
import {
  ARROWHEAD_LENGTH,
  type GraphLayout,
  type Point,
  STEP_H,
  STEP_POSITION_TIP_GAP,
  STEP_POSITION_TIP_H,
  STEP_POSITION_TIP_W,
  STEP_W,
  TERMINAL_SIZE,
  arrowheadPath,
  edgePreviewPosition,
  edgePreviewScale,
  edgeRoutePoints,
  edgeViewportCrossing,
  fitArrowheadLength,
  fittedView,
  isStepVisible,
  offsetPolyline,
  pointAlongPolyline,
  polylineLength,
  roundedPath,
  selfLoopGeometry,
  stepPositionTipSide,
  transitionFocusPoint,
  trimRouteEnd,
} from "./processGraphLayout";
import {
  type EdgeSelection,
  type ProcessEdge,
  type ProcessGraphNode,
  type ProcessInstancePath,
  type Selection,
  type StepReading,
  avgDuration,
  caseCount,
  departureCounts,
  edgeDeparturePercentage,
  edgeKey,
  occurrenceCount,
  processInstancePath,
  rankedTransitionWaits,
  resolveStepReading,
  terminalKind,
  totalDuration,
  truncate,
} from "./processGraphModel";
import { labelColors, totalDurationHeatScale } from "./processHeatScale";
import type { ProcessGraphState } from "./useProcessGraph";

const APP_LOGO_SIZE = 16;
const EDGE_STROKE_WIDTH = 1.5;
// The busiest transition's weight when the map is read as traffic; the rest scale down to
// EDGE_STROKE_WIDTH by the square root of their share, so a quiet route stays visible.
const VOLUME_MAX_STROKE_WIDTH = 7;
// How many of the longest waits carry a badge on the map while it is read as traffic.
const VOLUME_WAIT_BADGES = 4;
// A compared route carries meaning the default gradient doesn't, so it is drawn heavier.
const COMPARE_EDGE_STROKE_WIDTH = 2.5;
/** Width of each of the two lanes a route shared by both compared runs is drawn as. Their centres
 * sit exactly one width apart, so the pair touches and reads as one route of two colours. */
const COMPARE_EDGE_LANE_WIDTH = 2;
const COMPARE_EDGE_LANE_GAP = COMPARE_EDGE_LANE_WIDTH;
/** How far the split in a shared step box leans, top to bottom, from the box's centre line. */
const COMPARE_NODE_SPLIT_SLANT = 10;
// Resting routes use the same muted slate grey as the graph's supporting labels and terminal
// markers. Selection keeps this neutral grey too; only followed, compared and animated routes
// promote to their stronger cue.
const EDGE_COLOR = "var(--color-text-dim)";

// How much bigger the head on a followed route is than the default one.
const ARROWHEAD_FOCUS_SCALE = 1.4;

// Transparent hover/click target centered on the logo, larger than the icon so triggering the
// tooltip / app-highlight doesn't require pixel-precise aiming.
const APP_HIT_SIZE = 30;
// The same two most-used apps as the Steps table, with a four-pixel logo overlap. Each
// logo gets its own hit area so clicking the second app highlights its steps too.
const StepAppMarks = ({
  apps,
  x,
  y,
  color,
  onEnter,
  onLeave,
}: {
  apps: string[];
  x: number;
  y: number;
  color: string;
  onEnter?: (event: React.MouseEvent, app: string) => void;
  onLeave?: () => void;
}) => {
  let nextX = x;
  return (
    <g>
      {apps.slice(0, 2).map((app, index, displayed) => {
        const iconUri = appIconUri(app);
        const markX = nextX;
        const width = iconUri ? APP_LOGO_SIZE : (app.length + 2) * 5;
        const advance = width + (iconUri ? -4 : 4);
        nextX += advance;
        const hitPadding = (APP_HIT_SIZE - APP_LOGO_SIZE) / 2;
        return (
          <g key={app}>
            {iconUri ? (
              <image
                href={iconUri}
                x={markX}
                y={y - APP_LOGO_SIZE / 2}
                width={APP_LOGO_SIZE}
                height={APP_LOGO_SIZE}
                preserveAspectRatio="xMidYMid meet"
                pointerEvents="none"
              />
            ) : (
              <text
                x={markX}
                y={y}
                textAnchor="start"
                dominantBaseline="middle"
                fill={color}
                fontSize={9}
                pointerEvents="none"
              >
                ({app})
              </text>
            )}
            {onEnter && (
              <rect
                data-app={app}
                x={markX - (index === 0 ? hitPadding : 0)}
                y={y - APP_HIT_SIZE / 2}
                width={
                  (index === displayed.length - 1 ? width + hitPadding : advance) +
                  (index === 0 ? hitPadding : 0)
                }
                height={APP_HIT_SIZE}
                fill="transparent"
                pointerEvents="all"
                onMouseEnter={event => onEnter(event, app)}
                onMouseLeave={onLeave}
              />
            )}
          </g>
        );
      })}
    </g>
  );
};

// Breathing room between a cell and the hint above it, and a rough height for the hint used only
// to decide whether it clears the top of the viewport before it has been measured.
const NODE_TIP_GAP = 10;
const NODE_TIP_HEIGHT_ESTIMATE = 28;
const TERMINAL_PREVIEW_W = 84;
const TERMINAL_PREVIEW_H = 34;
// One click of the zoom buttons; the wheel is continuous.
const ZOOM_STEP = 1.25;
const PROCESS_TRANSITION_CUE_MS = 1000;
const STEP_POSITION_TOOLTIP_MS = 800;
const STEP_DOUBLE_CLICK_MS = 500;

type StepClick = { id: string; at: number };
type HoveredEdge = Pick<EdgeSelection, "from" | "to">;

// Pointer capture keeps pan/zoom reliable, but it can retarget the browser's later `dblclick`
// event to the SVG. Detect the repeated step press while we still have the hit-tested node id.
export const isRepeatedStepClick = (previous: StepClick | null, id: string, at: number) =>
  previous !== null &&
  previous.id === id &&
  at - previous.at >= 0 &&
  at - previous.at <= STEP_DOUBLE_CLICK_MS;

// Opacity applied to everything outside the highlighted app group (see emphasizedApp).
const EMPHASIS_DIM = 0.3;

// Pan/zoom limits for the map viewport.
const MIN_K = 0.1;
const MAX_K = 4;
// How much of the graph must stay inside the viewport on each axis when panning, so the map can
// never be scrolled away entirely.
const MIN_VISIBLE_PX = 96;
const HEAT_LEGEND_GRADIENT =
  "linear-gradient(to right, #ffffff, #e8f0f7, #c8dcee, #9dbcdd, #6e9ad0)";

const BoundaryGlyph = ({
  kind,
  label,
  x,
  y,
  radius = TERMINAL_SIZE / 2,
}: {
  kind: "start" | "end";
  label?: string;
  x: number;
  y: number;
  radius?: number;
}) => (
  <g aria-hidden="true">
    <circle
      cx={x}
      cy={y}
      r={radius}
      fill="var(--color-surface-strong)"
      stroke="var(--color-text-mid)"
      strokeWidth={2}
    />
    {label ? (
      <text
        x={x}
        y={kind === "start" ? y - radius - 8 : y + radius + 8}
        textAnchor="middle"
        dominantBaseline={kind === "start" ? "auto" : "hanging"}
        fill="var(--color-text-dim)"
        fontFamily="inherit"
        fontSize={11}
        fontWeight={600}
        letterSpacing="0.08em"
        pointerEvents="none"
      >
        {label.toLocaleUpperCase()}
      </text>
    ) : null}
  </g>
);

// A value that clears itself after `ms` — the shape of every transient cue on the map (search
// halo, transition pulse, step-position flash). Showing again restarts the clock; the timer is
// owned here so each cue can't leak one.
const useTransientValue = <T,>(ms: number) => {
  const [value, setValue] = useState<T | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clear = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setValue(null);
  }, []);
  const show = useCallback(
    (next: T) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      setValue(next);
      timerRef.current = setTimeout(() => setValue(null), ms);
    },
    [ms],
  );
  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );
  return [value, show, clear] as const;
};

// What the surrounding composition can ask the viewport to do.
export type ProcessGraphCanvasHandle = {
  centerOnNode: (id: string, animationMs?: number) => void;
  centerOnEdge: (from: string, to: string, animationMs?: number) => void;
  /** Briefly trace a transition without replacing the destination step selection. */
  pulseTransition: (
    from: string,
    to: string,
    destination: string,
    /** While comparing, whose run is being travelled — the dots take that side's colour. */
    side?: CompareSide | null,
  ) => boolean;
  /** Briefly label a step's position directly above its card. */
  showStepPosition: (nodeId: string, current: number, total: number) => void;
  /** Briefly mark a step as the first or last one of the instance, above its card. */
  showStepBoundary: (nodeId: string, kind: "start" | "end") => void;
};

type ProcessGraphCanvasProps = {
  expanded?: boolean;
  onToggleExpanded?: () => void;
  state: ProcessGraphState;
  peopleOptions?: UserFilterOption[];
  /** Persistent selection context centred above the map, when a step occurrence is active. */
  /** Transition currently inspected in the sidebar, kept separate from the selected step. */
  selectedTransition: EdgeSelection | null;
  /** A step/transition was picked on the map — reveal it in the side panel. */
  onSelect: (selection: Selection) => void;
  /** A step was double-clicked: open its inspector, even if another sidebar tab is active. */
  onOpen: (selection: Selection) => void;
  /** Empty space was clicked: drop the selection without reopening a collapsed panel. */
  onClearSelection: () => void;
};

const ControlTooltip = ({
  label,
  contentClassName,
  children,
}: {
  label: string;
  contentClassName?: string;
  children: React.ReactElement;
}) => (
  <Tooltip.Root>
    <Tooltip.Trigger asChild>{children}</Tooltip.Trigger>
    <Tooltip.Portal>
      <Tooltip.Content
        side="bottom"
        sideOffset={6}
        className={`pd-theme z-50 rounded-md border border-outline bg-surface px-2 py-1 text-xs text-text shadow-lift ${contentClassName ?? ""}`}
      >
        {label}
      </Tooltip.Content>
    </Tooltip.Portal>
  </Tooltip.Root>
);

// For the settings whose names don't explain themselves: a hint next to the label.
const SettingHelp = ({ label }: { label: string }) => (
  <Tooltip.Provider delayDuration={150}>
    <ControlTooltip label={label} contentClassName="max-w-64 leading-snug text-text-mid">
      <button
        type="button"
        aria-label={label}
        className="inline-flex items-center justify-center rounded-full text-text-dim outline-none hover:text-text focus-visible:ring-2 focus-visible:ring-info/25"
      >
        <CircleQuestionMark className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </ControlTooltip>
  </Tooltip.Provider>
);

const ProcessGraphCanvas = forwardRef<ProcessGraphCanvasHandle, ProcessGraphCanvasProps>(
  (
    {
      state,
      selectedTransition,
      onSelect,
      onOpen,
      onClearSelection,
      expanded,
      onToggleExpanded,
      peopleOptions,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const simplifyLimitId = useId();
    const selectedNodeShadowId = `selected-node-shadow-${useId()}`;
    const edgePreviewShadowId = `edge-preview-shadow-${useId()}`;
    const compareClipPrefix = `compare-node-clip-${useId()}`;
    const {
      graph,
      layout,
      nodes,
      edges,
      layoutPending,
      layoutFailed,
      selection,
      selectedInstanceIds,
      colorBy,
      setColorBy,
      stepReading,
      setStepReading,
      edgeRouting,
      setEdgeRouting,
      traffic,
      setTraffic,
      highlightedInstanceId,
      highlightedInstance,
      setHighlightedInstanceId,
      branchHoverEdge,
      mapFocus,
      comparePair,
    } = state;

    // Clicking a step's app logo highlights every step using that app by dimming the
    // rest of the graph; null = no highlight active.
    const [emphasizedApp, setEmphasizedApp] = useState<string | null>(null);
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchMatchIndex, setSearchMatchIndex] = useState(-1);
    const [searchPulse, showSearchPulse, clearSearchPulse] = useTransientValue<{
      nodeId: string;
    }>(900);
    const [transitionPulse, showTransitionPulse] = useTransientValue<{
      from: string;
      to: string;
      reverse: boolean;
      run: number;
      side: CompareSide | null;
    }>(PROCESS_TRANSITION_CUE_MS);
    const [stepPositionTooltip, showStepPositionTooltip] = useTransientValue<{
      nodeId: string;
      label: string;
    }>(STEP_POSITION_TOOLTIP_MS);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const transitionPulseRunRef = useRef(0);
    // Custom hover tooltip for the app logo (app name), positioned in viewport pixels relative to
    // the SVG. Replaces the native <title> so it can be dark, instant, and use a larger hit area.
    const [appTip, setAppTip] = useState<{ app: string; x: number; y: number } | null>(null);
    // Same bubble for the cells themselves: the map never says what a box is, and the native
    // <title> is both too slow and too quiet to be the thing that teaches it. It sits above the
    // cell rather than under the cursor, so the box it is naming stays unobscured.
    const [nodeTip, setNodeTip] = useState<{
      x: number;
      y: number;
      side: "above" | "below";
    } | null>(null);
    // The hint has one job, and opening a step is proof it landed. Once that has happened it stays
    // gone for the rest of the visit rather than nagging someone who already knows.
    const stepTipSpentRef = useRef(false);
    const [boundaryTip, setBoundaryTip] = useState<{
      kind: "start" | "end";
      x: number;
      y: number;
    } | null>(null);
    // Pointer feedback for the step boxes: the only cue that they are clickable at all, since
    // fill is already spoken for by the heat scale and by compare's ownership colours.
    const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);
    const [hoveredEdge, setHoveredEdge] = useState<HoveredEdge | null>(null);
    // Pointing at a branch row in the side panel is the same gesture as pointing at its route
    // on the map, so it feeds the same hover state rather than a second kind of emphasis.
    const pointedEdge = hoveredEdge ?? branchHoverEdge;
    const activeEdge = pointedEdge ?? selectedTransition;
    const activeEdgeKey = activeEdge ? edgeKey(activeEdge.from, activeEdge.to) : null;
    const selectedEndpointNodes = useMemo(() => {
      if (!selectedTransition) return [];
      const endpoints: Array<{ id: string; endpoint: "from" | "to" }> = [
        { id: selectedTransition.from, endpoint: "from" },
        ...(selectedTransition.to === selectedTransition.from
          ? []
          : [{ id: selectedTransition.to, endpoint: "to" as const }]),
      ];
      return endpoints.flatMap(({ id, endpoint }) => {
        const node = nodes.find(candidate => candidate.id === id);
        return node ? [{ node, endpoint, terminal: terminalKind(node, graph) }] : [];
      });
    }, [selectedTransition, nodes, graph]);

    const svgW = layout?.width ?? 0;
    const svgH = layout?.height ?? 0;
    // A map of only Start and End (no case in scope, as while a page loads) shows nothing worth
    // keeping on screen, so a layout replacing it is announced like a first one.
    const showsSteps = nodes.some(node => terminalKind(node, graph) === null);
    const departures = useMemo(() => departureCounts(nodes), [nodes]);
    const appsByNode = useMemo(
      () => new Map(nodes.map(node => [node.id, stepApps(node)])),
      [nodes],
    );
    // The only percentage on screen is the hovered edge's. Keep a position near each route's
    // source ready so hover can reveal it without the default map becoming a field of numbers.
    const labelPosByKey = useMemo(() => {
      const result = new Map<string, Point>();
      if (!layout) return result;
      for (const e of edges) {
        if (e.from === e.to) continue;
        const key = edgeKey(e.from, e.to);
        const points = layout.edgePoints.get(key);
        if (!points?.length) continue;
        const length = polylineLength(points);
        const position = pointAlongPolyline(points, Math.min(72, Math.max(18, length * 0.22)));
        if (position) result.set(key, position);
      }
      return result;
    }, [edges, layout]);

    // Every edge's routed geometry, built once per layout. It is hover-independent, while the SVG
    // re-renders on each hover/tooltip change — without the memo every mouse move would redo the
    // path building (and its string concatenation) for the whole graph.
    const edgeGeometry = useMemo(() => {
      const result = new Map<
        string,
        {
          isSelfLoop: boolean;
          routePoints: Point[];
          drawnPoints: Point[];
          pathD: string;
          fullPathD: string;
          arrowheadD: string | null;
        }
      >();
      for (const e of edges) {
        const key = edgeKey(e.from, e.to);
        const routePoints = edgeRoutePoints(layout, e.from, e.to);
        if (!routePoints || routePoints.length === 0) continue;
        // Stop the line where its arrowhead starts. Trimming the route (rather than drawing the
        // full line under the head) also pulls the final turn back, so the corner has finished
        // bending well before the arrow takes over. End transitions keep the full route: they
        // use the hollow terminal itself as their endpoint and carry no arrowhead.
        const drawnPoints =
          e.to === graph.end
            ? routePoints
            : trimRouteEnd(routePoints, fitArrowheadLength(routePoints));
        const endpoint = routePoints[routePoints.length - 1];
        const approach = routePoints[routePoints.length - 2];
        result.set(key, {
          isSelfLoop: e.from === e.to,
          routePoints,
          drawnPoints,
          pathD: roundedPath(drawnPoints),
          fullPathD: roundedPath(routePoints),
          arrowheadD:
            e.to !== graph.end && approach
              ? arrowheadPath(endpoint, approach, fitArrowheadLength(routePoints))
              : null,
        });
      }
      return result;
    }, [edges, layout, graph.end]);

    // Each node's occurrence split between the compared pair — one pass here instead of an
    // instances scan per node on every render.
    const compareStatsById = useMemo(
      () =>
        comparePair ? new Map(nodes.map(n => [n.id, compareNodeStats(n, comparePair)])) : null,
      [comparePair, nodes],
    );

    // The two compared routes, when a pair is set. Both are drawn at full strength: unlike a
    // focused instance, neither is an overlay on a surrounding process — the graph *is* the two
    // of them, so there is nothing left over to dim against.
    const comparePaths = useMemo(() => {
      if (!comparePair) return null;
      const paths = (side: CompareSide): ProcessInstancePath => {
        const ids = new Set(compareSideIds(comparePair, side));
        const routes = state.selectedInstances
          .filter(run => ids.has(run.id))
          .map(run => processInstancePath(run, graph));
        return {
          nodeIds: new Set(routes.flatMap(path => [...path.nodeIds])),
          edgeKeys: new Set(routes.flatMap(path => [...path.edgeKeys])),
        };
      };
      return { a: paths("a"), b: paths("b") };
    }, [comparePair, graph, state.selectedInstances]);
    const comparing = comparePaths !== null && comparePair !== null;

    // Heat range is the total-duration spread across the *visible* step nodes only (terminals keep
    // their ring style), so the ramp re-normalises as the min-transitions slider filters the graph.
    const durationHeat = useMemo(() => {
      const steps = nodes.filter(node => terminalKind(node, graph) === null);
      return totalDurationHeatScale(steps);
    }, [nodes, graph]);
    const durMin = durationHeat?.min ?? Number.POSITIVE_INFINITY;
    const durMax = durationHeat?.max ?? Number.NEGATIVE_INFINITY;
    // Comparing gives every cell a second job — saying which run visits it — and a duration tint
    // underneath would be a second colour scale competing for the same surface. The heat ramp
    // stands down for the duration; the two sides' own readings are on the cell either way.
    const heatOn = colorBy === "time" && durationHeat !== null && !comparing;

    const highlightedInstancePath = useMemo(
      () => (highlightedInstance ? processInstancePath(highlightedInstance, graph) : null),
      [graph, highlightedInstance],
    );
    // Following one of the compared runs keeps that run's colour rather than taking the generic
    // focus cue: while comparing, colour *is* the way the two are told apart, and a route that
    // changed hue on being followed would be saying the wrong thing.
    const highlightedCompareColor =
      comparePair && highlightedInstanceId
        ? compareSideIds(comparePair, "a").includes(highlightedInstanceId)
          ? compareSideColor("a")
          : compareSideIds(comparePair, "b").includes(highlightedInstanceId)
            ? compareSideColor("b")
            : null
        : null;
    // Selecting one step or one transition narrows the map exactly the way highlighting an
    // instance does: only the picked node (or the transition and its two endpoints) stays lit.
    const selectionPath = useMemo<ProcessInstancePath | null>(() => {
      const edge = selectedTransition ?? (selection?.kind === "edge" ? selection : null);
      // A selection is a place on the map, not a run through it, so it carries no chronology.
      if (edge)
        return {
          nodeIds: new Set([edge.from, edge.to]),
          edgeKeys: new Set([edgeKey(edge.from, edge.to)]),
        };
      if (selection?.kind === "node")
        return {
          nodeIds: new Set([selection.id]),
          edgeKeys: new Set<string>(),
        };
      return null;
    }, [selection, selectedTransition]);
    // An instance highlight outranks a selection, and both outrank what a panel tab is pointing
    // at, so the evidence open in the panel is always what the map shows.
    const focusPath = highlightedInstancePath ?? selectionPath ?? mapFocus;
    // Compared runs own the routes' colour and weight, so traffic sits out the comparison.
    const showVolume = !comparing && traffic;
    const volume = useMemo(() => {
      if (!showVolume) return null;
      const maxTransitions = Math.max(1, ...edges.map(edge => edge.transitionsCount));
      return {
        // The longest waits stay visible on a map read as traffic, as badges on their routes.
        waitByEdge: new Map(
          rankedTransitionWaits(edges, graph.start, graph.end)
            .slice(0, VOLUME_WAIT_BADGES)
            .map(wait => [edgeKey(wait.from, wait.to), wait.averageWaitSeconds]),
        ),
        strokeWidth: (transitions: number) =>
          EDGE_STROKE_WIDTH +
          (VOLUME_MAX_STROKE_WIDTH - EDGE_STROKE_WIDTH) * Math.sqrt(transitions / maxTransitions),
      };
    }, [edges, graph.end, graph.start, showVolume]);
    // Cases reaching each cell: the terminals' traffic reading, and the steps' "cases" reading.
    const casesById = useMemo(
      () => new Map(nodes.map(node => [node.id, caseCount(node)])),
      [nodes],
    );
    const reading = resolveStepReading(stepReading, volume !== null);
    // The pooled reading printed under a step's name. The off-screen copies of a transition's
    // endpoints print it too, so a copy never says something its cell doesn't.
    const stepReadingText = (node: ProcessGraphNode): string | null => {
      switch (reading) {
        case "visitsAvg":
          return `${occurrenceCount(node)}x · ${formatDuration(avgDuration(node))}`;
        case "totalTime":
          return t("processPanel.nodeTotalTime", {
            duration: formatDuration(totalDuration(node)),
          });
        case "cases": {
          const cases = casesById.get(node.id) ?? 0;
          return t("processPanel.nodeVolume", {
            count: cases,
            // Start's cases are all the cases the map counts, drawn or not.
            share: Math.round((cases / Math.max(1, casesById.get(graph.start) ?? 0)) * 100),
          });
        }
        case "none":
          return null;
      }
    };
    const isEmphasizing = emphasizedApp !== null || focusPath !== null;

    // --- pan / zoom: the graph lives in a transformed <g> inside a fixed-size viewport. We keep the
    // view transform in a ref and apply it imperatively so a drag/zoom doesn't re-render the SVG
    // contents (only the cheap transform attribute changes). Content re-renders re-apply it below.
    const svgRef = useRef<SVGSVGElement>(null);
    const viewportRef = useRef<SVGGElement>(null);
    const edgePreviewRefs = useRef(new Map<string, SVGGElement>());
    const topControlsRef = useRef<HTMLDivElement>(null);
    const heatLegendRef = useRef<HTMLDivElement>(null);
    // The zoom readout is written into the DOM the same way the transform is. Holding the
    // percentage in state instead would re-render the whole SVG on every wheel tick.
    const zoomInputRef = useRef<HTMLInputElement>(null);
    const view = useRef({ tx: 0, ty: 0, k: 1 });

    // Logo tooltip: show at the cursor (viewport pixels, for a position:fixed bubble) on enter,
    // clear on leave.
    const showAppTip = useCallback((e: React.MouseEvent, app: string) => {
      setAppTip({ app, x: e.clientX, y: e.clientY });
    }, []);
    const hideAppTip = useCallback(() => setAppTip(null), []);
    const showEdgeTip = useCallback((from: string, to: string) => {
      setHoveredEdge({ from, to });
    }, []);
    const hideEdgeTip = useCallback(() => setHoveredEdge(null), []);
    // Anchor the cell hint to the cell's own edge, not the cursor: the graph group carries the
    // pan/zoom transform, so its screen CTM turns layout coordinates into viewport pixels for the
    // position:fixed bubble. It sits centered above the cell and drops below it for cells near the
    // top of the viewport, where there is no room over the box.
    const showNodeTip = useCallback((nodeX: number, nodeY: number) => {
      if (stepTipSpentRef.current) return;
      const ctm = viewportRef.current?.getScreenCTM();
      if (!ctm) return;
      const midX = nodeX + STEP_W / 2;
      const above = new DOMPoint(midX, nodeY).matrixTransform(ctm);
      if (above.y - NODE_TIP_GAP - NODE_TIP_HEIGHT_ESTIMATE > 0) {
        setNodeTip({ x: above.x, y: above.y - NODE_TIP_GAP, side: "above" });
        return;
      }
      const below = new DOMPoint(midX, nodeY + STEP_H).matrixTransform(ctm);
      setNodeTip({ x: below.x, y: below.y + NODE_TIP_GAP, side: "below" });
    }, []);
    const hideNodeTip = useCallback(() => setNodeTip(null), []);

    // Keep selected transition endpoints represented even when their real cells are beyond the
    // viewport. These groups live outside the transformed graph group, so position them directly
    // in viewport pixels and slide around any map chrome occupying the same boundary.
    const paintEdgePreviews = useCallback(() => {
      const svg = svgRef.current;
      // Nothing selected means no previews to place; bail before any DOM measurement so a plain
      // re-render never pays for getBoundingClientRect.
      if (!svg || !layout || selectedEndpointNodes.length === 0) return;
      const viewport = { width: svg.clientWidth, height: svg.clientHeight };
      if (!viewport.width || !viewport.height) return;
      const svgRect = svg.getBoundingClientRect();
      const obstacles = [topControlsRef.current, heatLegendRef.current]
        .filter((element): element is HTMLDivElement => element !== null)
        .map(element => {
          const rect = element.getBoundingClientRect();
          return {
            left: rect.left - svgRect.left,
            top: rect.top - svgRect.top,
            right: rect.right - svgRect.left,
            bottom: rect.bottom - svgRect.top,
          };
        });
      const { tx, ty, k } = view.current;
      const previewScale = edgePreviewScale(k);
      const selectedRoute = selectedTransition
        ? edgeRoutePoints(layout, selectedTransition.from, selectedTransition.to)
        : null;
      const projectedRoute = selectedRoute?.map(point => ({
        x: tx + point.x * k,
        y: ty + point.y * k,
      }));

      for (const { node, endpoint, terminal } of selectedEndpointNodes) {
        const element = edgePreviewRefs.current.get(node.id);
        const graphCenter = layout.nodePos.get(node.id);
        if (!element || !graphCenter) continue;
        const projectedCenter = { x: tx + graphCenter.x * k, y: ty + graphCenter.y * k };
        const actualSize = terminal
          ? { width: TERMINAL_SIZE * k, height: TERMINAL_SIZE * k }
          : { width: STEP_W * k, height: STEP_H * k };
        // The real cell takes over immediately—even its first clipped border is a clearer spatial
        // cue than showing a duplicate preview beside it.
        if (isStepVisible(projectedCenter, actualSize, viewport)) {
          element.style.display = "none";
          continue;
        }
        const previewSize = terminal
          ? { width: TERMINAL_PREVIEW_W * previewScale, height: TERMINAL_PREVIEW_H * previewScale }
          : { width: STEP_W * previewScale, height: STEP_H * previewScale };
        const routeCrossing = projectedRoute
          ? edgeViewportCrossing(projectedRoute, endpoint, viewport)
          : null;
        const position = edgePreviewPosition(
          routeCrossing ?? projectedCenter,
          previewSize,
          viewport,
          obstacles,
        );
        element.style.display = "";
        element.setAttribute(
          "transform",
          `translate(${position.x},${position.y}) scale(${previewScale})`,
        );
        // A second hidden endpoint should not cover the first when both leave through the same
        // area of the map.
        obstacles.push({
          left: position.x - previewSize.width / 2,
          top: position.y - previewSize.height / 2,
          right: position.x + previewSize.width / 2,
          bottom: position.y + previewSize.height / 2,
        });
      }
    }, [layout, selectedEndpointNodes, selectedTransition]);

    // The map is bounded rather than infinite: a pan can never push the graph off-screen. Along each
    // axis the graph and the viewport must keep overlapping by `keep` pixels, so there is always
    // something to grab and pan back with. Small graphs (narrower/shorter than the viewport) may
    // still be moved around freely inside it, they just cannot leave.
    const clampView = useCallback(() => {
      const svg = svgRef.current;
      if (!svg || !svgW || !svgH) return;
      const vw = svg.clientWidth;
      const vh = svg.clientHeight;
      if (!vw || !vh) return;
      const { k } = view.current;
      const clampAxis = (t: number, content: number, viewportSize: number) => {
        const keep = Math.max(MIN_VISIBLE_PX, Math.min(content, viewportSize) * 0.5);
        return Math.max(keep - content, Math.min(t, viewportSize - keep));
      };
      view.current.tx = clampAxis(view.current.tx, svgW * k, vw);
      view.current.ty = clampAxis(view.current.ty, svgH * k, vh);
    }, [svgW, svgH]);

    // Push the view transform into the DOM directly — none of this goes through React.
    const paintView = useCallback(() => {
      clampView();
      const { tx, ty, k } = view.current;
      viewportRef.current?.setAttribute("transform", `translate(${tx},${ty}) scale(${k})`);
      paintEdgePreviews();
      if (zoomInputRef.current && document.activeElement !== zoomInputRef.current) {
        zoomInputRef.current.value = `${Math.round(k * 100)}%`;
      }
    }, [paintEdgePreviews, clampView]);

    const applyView = useCallback(
      (animationMs = 0) => {
        const g = viewportRef.current;
        // Interactions (pan/zoom) jump; programmatic recentering eases so the move is followable.
        if (g) g.style.transition = animationMs > 0 ? `transform ${animationMs}ms ease` : "none";
        // The cell hint was placed against the old transform, so any view change strands it away
        // from the cell it is naming. Drop it and let the next hover re-place it.
        setNodeTip(null);
        paintView();
      },
      [paintView],
    );

    // Returns false when the viewport measures 0×0 (a hidden tab); callers keep their pending fit
    // armed and the resize observer below finishes the job.
    const fitView = useCallback(() => {
      const svg = svgRef.current;
      if (!svg || !layout || layoutPending || !svgW || !svgH) return false;
      const vw = svg.clientWidth;
      const vh = svg.clientHeight;
      if (!vw || !vh) return false;
      view.current = fittedView(
        { width: svgW, height: svgH },
        { width: vw, height: vh },
        layout.nodePos.get(graph.start)?.x ?? svgW / 2,
      );
      applyView();
      return true;
    }, [svgW, svgH, applyView, layout, layoutPending, graph.start]);

    // The resize observer below is deliberately not re-subscribed on every layout change (it would
    // lose the size baseline it keeps), so it reaches the current fitView through a ref.
    const fitViewRef = useRef(fitView);
    useLayoutEffect(() => {
      fitViewRef.current = fitView;
    });

    // Re-apply the (persisted) transform after every content re-render so React's reconciliation of
    // the viewport children never drops it, and so the zoom readout is right on first paint.
    // paintView rather than applyView, so an in-flight recentering animation isn't cancelled by
    // resetting the transition.
    useLayoutEffect(paintView);

    // Fit when the first layout lands, and again after an instance switch (the effect below
    // re-arms it). Layout arrival is the trigger — not first paint — because the async layout
    // resolves after mount. Slider/color changes keep the user's current pan/zoom.
    const pendingFit = useRef(true);
    useEffect(() => {
      if (!layout || !pendingFit.current) return;
      // Consume the flag inside the frame callback, not when scheduling: if an unrelated
      // re-render re-runs this effect before the frame fires, the cleanup cancels the
      // frame and the still-armed flag lets the re-run schedule a new one.
      const id = requestAnimationFrame(() => {
        // Only consume the flag if the fit landed. On a tab that isn't showing, the viewport has
        // no size to fit to and the flag stays armed for the resize observer to pick up.
        if (fitView()) pendingFit.current = false;
      });
      return () => cancelAnimationFrame(id);
    }, [layout, fitView]);

    // When the viewport itself resizes (panel expand/collapse, divider drag, window resize), keep
    // the current transform unchanged. The SVG's origin does not move, so preserving the transform
    // keeps the map visually fixed and lets newly available space simply reveal more of it. The
    // observer remains necessary for an initial fit attempted while the viewport was hidden.
    useEffect(() => {
      const svg = svgRef.current;
      if (!svg || typeof ResizeObserver === "undefined") return;
      const observer = new ResizeObserver(entries => {
        const rect = entries[entries.length - 1]?.contentRect;
        if (!rect) return;
        if (pendingFit.current && rect.width > 0 && rect.height > 0) {
          if (fitViewRef.current()) pendingFit.current = false;
        } else {
          paintEdgePreviews();
        }
      });
      observer.observe(svg);
      return () => observer.disconnect();
    }, [paintEdgePreviews]);

    // Switching instances rebuilds the graph with a possibly different app set, so drop any
    // active app highlight rather than dim the whole new graph with nothing matching, and re-fit
    // once the new layout lands.
    useEffect(() => {
      setEmphasizedApp(null);
      setHoveredEdge(null);
      pendingFit.current = true;
    }, [selectedInstanceIds]);

    // Entering compare re-frames what the map is about: the pair, whole. The scope usually
    // changes with it and the effect above re-fits, but compare can also be entered on a scope
    // that already *is* the two instances (from one run's timeline, say), leaving the map wherever
    // the user had panned it. Fit on the way in either way.
    const wasComparing = useRef(comparing);
    useEffect(() => {
      const entering = comparing && !wasComparing.current;
      wasComparing.current = comparing;
      if (!entering) return;
      // Entering from a scope that narrows to the pair (the timeline's Compare button) kicks off
      // a fresh ELK layout that hasn't landed yet, so this frame can only fit the old layout.
      // Keep the flag armed no matter what this attempt does, so the fit effect above re-fits once
      // more — for real, this time — once the pair's own layout arrives.
      pendingFit.current = true;
      const id = requestAnimationFrame(fitViewRef.current);
      return () => cancelAnimationFrame(id);
    }, [comparing]);

    // App and instance emphasis are alternative readings of the same map. Activating an instance
    // always wins so two independent dimming rules never leave the route illegible.
    useEffect(() => {
      if (highlightedInstanceId) setEmphasizedApp(null);
    }, [highlightedInstanceId]);

    const zoomAt = useCallback(
      (mx: number, my: number, factor: number) => {
        const k0 = view.current.k;
        const k1 = Math.max(MIN_K, Math.min(k0 * factor, MAX_K));
        const f = k1 / k0;
        view.current.tx = mx - (mx - view.current.tx) * f;
        view.current.ty = my - (my - view.current.ty) * f;
        view.current.k = k1;
        applyView();
      },
      [applyView],
    );

    // Zoom buttons work on the viewport centre; the wheel zooms toward the cursor.
    const zoomByStep = useCallback(
      (factor: number) => {
        const svg = svgRef.current;
        if (!svg) return;
        zoomAt(svg.clientWidth / 2, svg.clientHeight / 2, factor);
      },
      [zoomAt],
    );

    const applyTypedZoom = useCallback(() => {
      const input = zoomInputRef.current;
      if (!input) return;
      const percentage = Number.parseFloat(input.value.replace(",", "."));
      if (Number.isFinite(percentage)) {
        const nextK = Math.max(MIN_K, Math.min(percentage / 100, MAX_K));
        zoomByStep(nextK / view.current.k);
      } else {
        input.value = `${Math.round(view.current.k * 100)}%`;
      }
    }, [zoomByStep]);

    // Recenter the viewport on a graph point (keeping the current zoom) with a short ease. Used when
    // the user picks a step/edge from the side panel, so the map follows the selection.
    const centerOn = useCallback(
      (x: number, y: number, animationMs = 250) => {
        const svg = svgRef.current;
        if (!svg) return;
        const vw = svg.clientWidth || 800;
        const vh = svg.clientHeight || 600;
        const k = view.current.k;
        view.current = { k, tx: vw / 2 - x * k, ty: vh / 2 - y * k };
        applyView(animationMs);
      },
      [applyView],
    );

    // A step clicked near the edge of the viewport is half-cut and leaves no room for the cues
    // drawn around it, so selecting one there recenters the map instead of leaving it clipped.
    // Clicks comfortably inside the viewport keep the user's framing untouched.
    const EDGE_REVEAL_MARGIN = 72;
    const revealIfNearEdge = useCallback(
      (id: string) => {
        const svg = svgRef.current;
        const pos = layout?.nodePos.get(id);
        if (!svg || !pos) return;
        const vw = svg.clientWidth;
        const vh = svg.clientHeight;
        if (!vw || !vh) return;
        const { tx, ty, k } = view.current;
        const node = nodes.find(candidate => candidate.id === id);
        const terminal = node ? terminalKind(node, graph) !== null : false;
        const halfW = ((terminal ? TERMINAL_SIZE : STEP_W) * k) / 2;
        const halfH = ((terminal ? TERMINAL_SIZE : STEP_H) * k) / 2;
        const cx = pos.x * k + tx;
        const cy = pos.y * k + ty;
        const clipped =
          cx - halfW < EDGE_REVEAL_MARGIN ||
          cx + halfW > vw - EDGE_REVEAL_MARGIN ||
          cy - halfH < EDGE_REVEAL_MARGIN ||
          cy + halfH > vh - EDGE_REVEAL_MARGIN;
        if (clipped) centerOn(pos.x, pos.y);
      },
      [layout, nodes, graph, centerOn],
    );

    // Search uses the full (untruncated) step name. Results follow graph order so repeated names
    // have a stable, predictable next/previous sequence even after navigating away and back.
    const searchMatches = useMemo(() => {
      const query = searchQuery.trim().toLocaleLowerCase();
      if (!query) return [];
      return nodes.filter(
        node => terminalKind(node, graph) === null && node.name.toLocaleLowerCase().includes(query),
      );
    }, [searchQuery, nodes, graph]);

    useEffect(() => {
      setSearchMatchIndex(-1);
      clearSearchPulse();
    }, [searchQuery, nodes, clearSearchPulse]);
    useEffect(() => {
      if (searchOpen) requestAnimationFrame(() => searchInputRef.current?.focus());
    }, [searchOpen]);

    const pulseTransition = useCallback(
      (from: string, to: string, destination: string, side: CompareSide | null = null) => {
        if (!edgeRoutePoints(layout, from, to)) return false;
        transitionPulseRunRef.current += 1;
        showTransitionPulse({
          from,
          to,
          reverse: from !== to && destination === from,
          run: transitionPulseRunRef.current,
          side,
        });
        return true;
      },
      [layout, showTransitionPulse],
    );

    const showStepLabel = useCallback(
      (nodeId: string, label: string) => {
        if (!nodeId) return;
        showStepPositionTooltip({ nodeId, label });
      },
      [showStepPositionTooltip],
    );

    const showStepPosition = useCallback(
      (nodeId: string, current: number, total: number) =>
        showStepLabel(nodeId, t("step_position_compact", { current, total })),
      [showStepLabel, t],
    );

    // Arrowing past either end of an instance keeps the selection where it is, so the same slot
    // says why nothing moved.
    const showStepBoundary = useCallback(
      (nodeId: string, kind: "start" | "end") =>
        showStepLabel(nodeId, t(kind === "start" ? "step_position_start" : "step_position_end")),
      [showStepLabel, t],
    );

    // An ordinary React-controlled halo instead of SVG animation: embedded webviews differ
    // in SMIL support, while a static overlay is guaranteed to remain visible for the full cue.
    const pulseSearchMatch = useCallback(
      (nodeId: string) => showSearchPulse({ nodeId }),
      [showSearchPulse],
    );

    const moveToSearchMatch = useCallback(
      (direction: 1 | -1) => {
        if (searchMatches.length === 0) return;
        const next =
          searchMatchIndex < 0
            ? direction === 1
              ? 0
              : searchMatches.length - 1
            : (searchMatchIndex + direction + searchMatches.length) % searchMatches.length;
        const pos = layout?.nodePos.get(searchMatches[next].id);
        if (pos) centerOn(pos.x, pos.y);
        pulseSearchMatch(searchMatches[next].id);
        setSearchMatchIndex(next);
      },
      [searchMatches, searchMatchIndex, layout, centerOn, pulseSearchMatch],
    );

    const closeSearch = useCallback(() => {
      setSearchOpen(false);
      setSearchQuery("");
      setSearchMatchIndex(-1);
      clearSearchPulse();
    }, [clearSearchPulse]);

    useImperativeHandle(
      ref,
      () => ({
        centerOnNode: (id: string, animationMs?: number) => {
          const pos = layout?.nodePos.get(id);
          if (pos) centerOn(pos.x, pos.y, animationMs);
        },
        centerOnEdge: (from: string, to: string, animationMs?: number) => {
          const pos = transitionFocusPoint(layout, from, to);
          if (pos) {
            // Revealing All paths may auto-fit a large map down to 20%. A transition
            // picked from the panel needs a readable route; off-screen endpoints have previews.
            view.current.k = Math.max(view.current.k, 0.6);
            centerOn(pos.x, pos.y, animationMs);
          }
        },
        pulseTransition,
        showStepPosition,
        showStepBoundary,
      }),
      [layout, centerOn, pulseTransition, showStepPosition, showStepBoundary],
    );

    // Wheel and Safari gesture events must be non-passive to preventDefault, so they're attached
    // imperatively rather than via React props (which registers wheel as passive).
    useEffect(() => {
      const svg = svgRef.current;
      if (!svg) return;
      const onWheel = (e: WheelEvent) => {
        e.preventDefault();
        setHoveredEdge(null);
        const r = svg.getBoundingClientRect();
        const mx = e.clientX - r.left;
        const my = e.clientY - r.top;
        if (e.ctrlKey) {
          // Trackpad pinch arrives as wheel + ctrlKey on Chrome/Firefox/Edge.
          zoomAt(mx, my, Math.min(2, Math.max(0.5, Math.exp(-e.deltaY * 0.01))));
        } else {
          view.current.tx -= e.deltaX;
          view.current.ty -= e.deltaY;
          applyView();
        }
      };
      let gestureStartK = 1;
      const onGestureStart = (e: Event) => {
        e.preventDefault();
        gestureStartK = view.current.k;
      };
      const onGestureChange = (e: Event) => {
        e.preventDefault();
        const ge = e as Event & { scale: number; clientX?: number; clientY?: number };
        const r = svg.getBoundingClientRect();
        const mx = (ge.clientX ?? r.left + r.width / 2) - r.left;
        const my = (ge.clientY ?? r.top + r.height / 2) - r.top;
        const k1 = Math.max(MIN_K, Math.min(gestureStartK * ge.scale, MAX_K));
        zoomAt(mx, my, k1 / view.current.k);
      };
      svg.addEventListener("wheel", onWheel, { passive: false });
      svg.addEventListener("gesturestart", onGestureStart);
      svg.addEventListener("gesturechange", onGestureChange);
      return () => {
        svg.removeEventListener("wheel", onWheel);
        svg.removeEventListener("gesturestart", onGestureStart);
        svg.removeEventListener("gesturechange", onGestureChange);
      };
    }, [applyView, zoomAt]);

    // Drag-to-pan vs click-to-select, told apart by a small movement threshold.
    const drag = useRef({
      active: false,
      moved: false,
      startX: 0,
      startY: 0,
      startTx: 0,
      startTy: 0,
      pressed: null as Selection | null,
      pressedApp: null as string | null,
      pressedPreview: false,
    });
    const lastStepClick = useRef<StepClick | null>(null);

    const onPointerDown = (e: React.PointerEvent<SVGSVGElement>) => {
      // The SVG is a draggable canvas, not selectable document content. Without cancelling the
      // browser's default gesture, even a tiny pan across the canvas can text-select a swath of
      // edge-percentage labels (and leave their blue native-selection highlight behind).
      e.preventDefault();
      window.getSelection()?.removeAllRanges();
      drag.current.active = true;
      drag.current.moved = false;
      drag.current.startX = e.clientX;
      drag.current.startY = e.clientY;
      drag.current.startTx = view.current.tx;
      drag.current.startTy = view.current.ty;
      // The app logo is its own gesture (highlight the app group), so test it before the node body
      // it sits inside. Nodes then sit above edges, so test those next; fall back to the edge.
      const target = e.target as Element;
      const appHit = target.closest?.("[data-app]");
      const nodeHit = target.closest?.("[data-node-id]");
      const previewHit = target.closest?.("[data-edge-endpoint-preview]");
      if (appHit) {
        drag.current.pressedApp = appHit.getAttribute("data-app");
        drag.current.pressed = null;
        drag.current.pressedPreview = false;
      } else if (nodeHit) {
        drag.current.pressedApp = null;
        drag.current.pressed = { kind: "node", id: nodeHit.getAttribute("data-node-id") ?? "" };
        drag.current.pressedPreview = previewHit !== null;
      } else {
        drag.current.pressedApp = null;
        drag.current.pressedPreview = false;
        const edgeHit = target.closest?.("[data-edge-from]");
        drag.current.pressed = edgeHit
          ? {
              kind: "edge",
              from: edgeHit.getAttribute("data-edge-from") ?? "",
              to: edgeHit.getAttribute("data-edge-to") ?? "",
            }
          : null;
      }
      try {
        svgRef.current?.setPointerCapture(e.pointerId);
      } catch {
        /* capture is best-effort */
      }
    };

    const onPointerMove = (e: React.PointerEvent<SVGSVGElement>) => {
      if (!drag.current.active) return;
      const dx = e.clientX - drag.current.startX;
      const dy = e.clientY - drag.current.startY;
      if (!drag.current.moved && Math.abs(dx) + Math.abs(dy) > 3) {
        drag.current.moved = true;
        setHoveredEdge(null);
      }
      view.current.tx = drag.current.startTx + dx;
      view.current.ty = drag.current.startTy + dy;
      applyView();
    };

    const endDrag = (e: React.PointerEvent<SVGSVGElement>) => {
      if (!drag.current.active) return;
      drag.current.active = false;
      try {
        svgRef.current?.releasePointerCapture(e.pointerId);
      } catch {
        /* release is best-effort */
      }
      // A click, not a pan.
      if (!drag.current.moved) {
        if (drag.current.pressedApp !== null) {
          lastStepClick.current = null;
          // Toggle the app-group highlight; clicking the same logo again clears it.
          const app = drag.current.pressedApp;
          setHighlightedInstanceId(null);
          setEmphasizedApp(current => (current === app ? null : app));
        } else if (drag.current.pressed !== null) {
          const pressed = drag.current.pressed;
          if (pressed.kind === "node") {
            // The click the hint was asking for: it has been read, so retire it.
            stepTipSpentRef.current = true;
            setNodeTip(null);
            if (drag.current.pressedPreview) {
              lastStepClick.current = null;
              onSelect(pressed);
              const position = layout?.nodePos.get(pressed.id);
              if (position) centerOn(position.x, position.y);
            } else if (isRepeatedStepClick(lastStepClick.current, pressed.id, e.timeStamp)) {
              lastStepClick.current = null;
              onOpen(pressed);
            } else {
              lastStepClick.current = { id: pressed.id, at: e.timeStamp };
              // A single step click keeps its tab-specific behavior (including Ask context).
              onSelect(pressed);
              revealIfNearEdge(pressed.id);
            }
          } else {
            lastStepClick.current = null;
            onSelect(pressed);
          }
        } else {
          lastStepClick.current = null;
          // A click on empty space clears the selection and any active highlight, but leaves a
          // collapsed panel closed — the expand band is the way to bring it back.
          onClearSelection();
          setEmphasizedApp(null);
          setHighlightedInstanceId(null);
        }
      }
    };

    const segBtn = (active: boolean) =>
      `px-3 py-0.5 ${active ? "bg-primary text-white" : "bg-surface text-text-dim hover:bg-surface-strong"}`;

    const hoveredEdgeData = pointedEdge
      ? edges.find(edge => edge.from === pointedEdge.from && edge.to === pointedEdge.to)
      : undefined;
    const hoveredEdgePosition = pointedEdge
      ? pointedEdge.from === pointedEdge.to
        ? (() => {
            const center = layout?.nodePos.get(pointedEdge.from);
            return center ? selfLoopGeometry(center).label : null;
          })()
        : (labelPosByKey.get(edgeKey(pointedEdge.from, pointedEdge.to)) ?? null)
      : null;
    const hoveredEdgePercentage = hoveredEdgeData
      ? edgeDeparturePercentage(hoveredEdgeData, departures)
      : null;

    return (
      /* overflow-hidden: panning replaces scrolling, so the map can move in all directions.
         data-process-graph: surfaces floating over the map (the scope picker's step filter) tell
         a click on the graph from a click that should dismiss them. */
      <div
        data-process-graph=""
        className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-surface"
      >
        <div className="relative min-h-0 flex-1">
          <svg
            ref={svgRef}
            className="block h-full w-full touch-none select-none bg-canvas [overscroll-behavior:none] cursor-grab active:cursor-grabbing"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
          >
            <defs>
              <filter
                id={selectedNodeShadowId}
                x="-25%"
                y="-50%"
                width="150%"
                height="210%"
                colorInterpolationFilters="sRGB"
              >
                <feDropShadow
                  dx={0}
                  dy={2.5}
                  stdDeviation={4.5}
                  floodColor={EDGE_COLOR}
                  floodOpacity={0.24}
                />
              </filter>
              <filter
                id={edgePreviewShadowId}
                x="-25%"
                y="-60%"
                width="150%"
                height="220%"
                colorInterpolationFilters="sRGB"
              >
                <feDropShadow
                  dx={0}
                  dy={3}
                  stdDeviation={5}
                  floodColor="var(--color-text)"
                  floodOpacity={0.24}
                />
              </filter>
            </defs>

            {/* Behind everything, and deliberately hit-testable: a press here finds no node or
                edge above it, which is exactly how a click on empty space clears the selection. */}
            <rect x={0} y={0} width="100%" height="100%" fill="transparent" />

            {/* All graph content lives here; the pan/zoom transform is applied to this group.
                Its client rect is the drawn diagram's on-screen bounds, which the tour uses
                to anchor cards beside the diagram rather than the full-bleed canvas. */}
            <g ref={viewportRef} data-tour="process-graph-diagram">
              {edges.map(e => {
                const key = edgeKey(e.from, e.to);
                const geometry = edgeGeometry.get(key);
                if (!geometry) return null;
                const { isSelfLoop, routePoints, drawnPoints, pathD, fullPathD } = geometry;
                const isEdgeSelected =
                  selectedTransition?.from === e.from && selectedTransition.to === e.to;
                const isEdgeActive = activeEdgeKey === key;
                const isEdgeEmphasized = isEdgeSelected || isEdgeActive;
                const activeTransitionPulse =
                  transitionPulse?.from === e.from && transitionPulse.to === e.to
                    ? transitionPulse
                    : null;
                const isTransitionPulsing = activeTransitionPulse !== null;
                // While comparing, the cue belongs to one run: it travels in that side's colour,
                // so the dots and the box they arrive at say the same thing about whose run this
                // is. Outside compare it keeps the neutral cue colour.
                const transitionCueColor = activeTransitionPulse?.side
                  ? compareSideColor(activeTransitionPulse.side)
                  : "var(--color-transition-cue)";
                const isHighlightedInstanceEdge =
                  highlightedInstancePath?.edgeKeys.has(key) ?? false;
                // The cue runs the whole route, not the trimmed line: the dots are the run
                // arriving, so they have to carry through the arrowhead to the box rather
                // than stopping short of it and leaving the last leg unvisited.
                const transitionPathD = activeTransitionPulse?.reverse
                  ? roundedPath([...routePoints].reverse())
                  : fullPathD;
                // While comparing, a route belongs to one run, to the other, or to both. A shared
                // route is drawn as two solid lanes running side by side — each side keeps an
                // unbroken line of its own colour, which a dashed overlay could not give both.
                const compareEdge = comparePaths
                  ? {
                      a: comparePaths.a.edgeKeys.has(key),
                      b: comparePaths.b.edgeKeys.has(key),
                    }
                  : null;
                const compareStroke =
                  compareEdge && (compareEdge.a || compareEdge.b)
                    ? compareSideColor(compareEdge.a ? "a" : "b")
                    : null;
                const compareShared = compareEdge !== null && compareEdge.a && compareEdge.b;
                // Self-loops are drawn from a canned path rather than routed points, so their
                // shared case falls back to concentric lanes: B's colour cored inside A's.
                const compareLanes =
                  compareShared && !isSelfLoop
                    ? [
                        {
                          side: "a" as const,
                          d: roundedPath(offsetPolyline(drawnPoints, -COMPARE_EDGE_LANE_GAP / 2)),
                        },
                        {
                          side: "b" as const,
                          d: roundedPath(offsetPolyline(drawnPoints, COMPARE_EDGE_LANE_GAP / 2)),
                        },
                      ]
                    : null;
                // A followed run is drawn in a firm grey, not the cue colour: the route is the
                // shape of the run and wants to read more strongly than the resting edges without
                // going full ink, which turns the whole path harshly black. The cue colour is
                // left to the one thing that is actually happening — the dots crossing the
                // transition being navigated — which is what lets it be seen on the route at all.
                const visibleStroke = isHighlightedInstanceEdge
                  ? (highlightedCompareColor ?? "var(--color-text-mid)")
                  : isTransitionPulsing
                    ? transitionCueColor
                    : isEdgeEmphasized
                      ? EDGE_COLOR
                      : (compareStroke ?? EDGE_COLOR);
                const restingStrokeWidth = volume
                  ? volume.strokeWidth(e.transitionsCount)
                  : EDGE_STROKE_WIDTH;
                const visibleStrokeWidth = isHighlightedInstanceEdge
                  ? 2.5
                  : isTransitionPulsing
                    ? 2
                    : isEdgeEmphasized
                      ? Math.max(3.5, restingStrokeWidth)
                      : compareStroke
                        ? COMPARE_EDGE_STROKE_WIDTH
                        : restingStrokeWidth;
                // The lanes replace the single stroke only while nothing else has claimed the
                // edge's colour — a pulse or a followed run still overrides the comparison.
                const drawCompareLanes = compareLanes !== null && visibleStroke === compareStroke;
                return (
                  <g
                    key={key}
                    data-edge-from={e.from}
                    data-edge-to={e.to}
                    className="cursor-pointer"
                    opacity={
                      focusPath
                        ? focusPath.edgeKeys.has(key)
                          ? 1
                          : EMPHASIS_DIM
                        : isEmphasizing
                          ? EMPHASIS_DIM
                          : 1
                    }
                    onMouseEnter={() => showEdgeTip(e.from, e.to)}
                    onMouseLeave={hideEdgeTip}
                  >
                    {/* Wide transparent stroke widens the click target for the thin visible edge. */}
                    <path
                      d={pathD}
                      fill="none"
                      stroke="transparent"
                      strokeWidth={14}
                      pointerEvents="stroke"
                    />
                    {drawCompareLanes ? (
                      compareLanes?.map(lane => (
                        <path
                          key={lane.side}
                          d={lane.d}
                          fill="none"
                          stroke={compareSideColor(lane.side)}
                          strokeWidth={COMPARE_EDGE_LANE_WIDTH}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          pointerEvents="none"
                        />
                      ))
                    ) : (
                      <path
                        d={pathD}
                        fill="none"
                        stroke={visibleStroke}
                        strokeWidth={visibleStrokeWidth}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        pointerEvents="none"
                      />
                    )}
                    {/* A shared self-loop has no routed points to offset: B is cored inside A. */}
                    {compareShared && compareLanes === null && visibleStroke === compareStroke && (
                      <path
                        d={pathD}
                        fill="none"
                        stroke={compareSideColor("b")}
                        strokeWidth={visibleStrokeWidth / 2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        pointerEvents="none"
                      />
                    )}
                    {activeTransitionPulse && (
                      <g
                        key={activeTransitionPulse.run}
                        className="process-transition-pulse"
                        pointerEvents="none"
                        aria-hidden="true"
                      >
                        <path
                          className="process-transition-pulse-halo"
                          d={transitionPathD}
                          fill="none"
                          style={{ stroke: transitionCueColor }}
                          strokeWidth={13}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          className="process-transition-pulse-line"
                          d={transitionPathD}
                          fill="none"
                          style={{ stroke: transitionCueColor }}
                          strokeWidth={6}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </g>
                    )}
                  </g>
                );
              })}

              {/* Paint the sole hover percentage after every route. SVG uses document order for
                  stacking, so no later edge can cross through this badge. */}
              {hoveredEdgePosition && hoveredEdgePercentage !== null && (
                <g pointerEvents="none">
                  <rect
                    x={hoveredEdgePosition.x + 2}
                    y={hoveredEdgePosition.y - 16}
                    width={34}
                    height={16}
                    rx={4}
                    fill="var(--color-surface)"
                    stroke="var(--color-text)"
                    strokeWidth={0.75}
                  />
                  <text
                    x={hoveredEdgePosition.x + 6}
                    y={hoveredEdgePosition.y - 4}
                    fill="var(--color-text)"
                    fontSize={10}
                    fontWeight={700}
                  >
                    {hoveredEdgePercentage}%
                  </text>
                </g>
              )}

              {volume &&
                [...volume.waitByEdge].map(([key, seconds]) => {
                  const position = labelPosByKey.get(key);
                  if (!position) return null;
                  const label = formatDuration(seconds);
                  const width = label.length * 6 + 12;
                  const dimmed = focusPath !== null && !focusPath.edgeKeys.has(key);
                  return (
                    <g
                      key={`wait:${key}`}
                      data-testid="map-wait-badge"
                      pointerEvents="none"
                      opacity={dimmed ? EMPHASIS_DIM : 1}
                    >
                      <rect
                        x={position.x + 2}
                        y={position.y - 17}
                        width={width}
                        height={16}
                        rx={8}
                        fill="var(--color-attention-soft)"
                        stroke="var(--color-warning)"
                        strokeWidth={0.75}
                      />
                      <text
                        x={position.x + 2 + width / 2}
                        y={position.y - 5}
                        textAnchor="middle"
                        fill="var(--color-warning-strong)"
                        fontSize={10}
                        fontWeight={600}
                      >
                        {label}
                      </text>
                    </g>
                  );
                })}

              {nodes.map((n, nodeIndex) => {
                const pos = layout?.nodePos.get(n.id);
                if (!pos) return null;
                const isSelected = selection?.kind === "node" && selection.id === n.id;
                const term = terminalKind(n, graph);
                // Dim anything outside the highlighted app group or focused instance path;
                // terminals carry no app, so they dim too whenever an app highlight is active.
                const dim =
                  (focusPath !== null && !focusPath.nodeIds.has(n.id)) ||
                  (focusPath === null &&
                    emphasizedApp !== null &&
                    !appsByNode.get(n.id)?.includes(emphasizedApp));

                if (term !== null) {
                  return (
                    <g
                      key={n.id}
                      role="img"
                      aria-label={t(term)}
                      className="cursor-default"
                      opacity={dim ? EMPHASIS_DIM : 1}
                      onMouseEnter={event =>
                        setBoundaryTip({ kind: term, x: event.clientX, y: event.clientY })
                      }
                      onMouseLeave={() => setBoundaryTip(null)}
                    >
                      <circle cx={pos.x} cy={pos.y} r={14} fill="transparent" pointerEvents="all" />
                      <BoundaryGlyph
                        kind={term}
                        label={volume ? `${t(term)} · ${casesById.get(n.id) ?? 0}` : t(term)}
                        x={pos.x}
                        y={pos.y}
                      />
                    </g>
                  );
                }

                const x = pos.x - STEP_W / 2;
                const y = pos.y - STEP_H / 2;
                const nodeReading = stepReadingText(n);
                const apps = appsByNode.get(n.id) ?? [];
                // Which run(s) this cell belongs to, and what each spent here. A side with no
                // visits is left out entirely, so a one-sided cell states "only this run comes
                // here" without a placeholder for the run that doesn't.
                const nodeCompare = compareStatsById?.get(n.id) ?? null;
                const nodeCompareSides = nodeCompare ? compareNodeSides(nodeCompare) : [];
                // While comparing, ownership is the cell's loudest reading, so it is carried by
                // the whole box rather than by a bar on its edge: solid in the owner's colour, or
                // split down the middle when both runs come here — left half A, right half B.
                const compareFill = nodeCompareSides.length > 0 ? nodeCompareSides[0] : null;
                const compareSplitSide = nodeCompareSides.length > 1 ? nodeCompareSides[1] : null;
                const compareClipId = `${compareClipPrefix}-${nodeIndex}`;
                const fill = compareFill
                  ? compareSideFill(compareFill)
                  : heatOn
                    ? (durationHeat?.colorById.get(n.id) ?? "var(--color-background-alt)")
                    : isSelected
                      ? "var(--color-surface-strong)"
                      : "var(--color-background-alt)";
                const label = compareFill
                  ? { title: "var(--color-text)", sub: "var(--color-text-mid)" }
                  : heatOn
                    ? labelColors(fill)
                    : { title: "var(--color-text)", sub: "var(--color-text-dim)" };
                // Rest < hover < selected, carried by the border alone so hover can never be
                // mistaken for a selection: selection keeps the heavier neutral outline and shadow.
                const isHovered =
                  (hoveredNodeId === n.id || pointedEdge?.to === n.id) && !isSelected;
                const nodeStrokeWidth = isSelected ? 2.25 : isHovered ? 1.6 : 0.7;
                const nodeStrokeOpacity = isSelected || isHovered ? 1 : 0.78;
                return (
                  <g
                    key={n.id}
                    data-node-id={n.id}
                    className="cursor-pointer"
                    onMouseEnter={() => {
                      setHoveredNodeId(n.id);
                      showNodeTip(x, y);
                    }}
                    onMouseLeave={() => {
                      setHoveredNodeId(current => (current === n.id ? null : current));
                      hideNodeTip();
                    }}
                    opacity={dim && searchPulse?.nodeId !== n.id ? EMPHASIS_DIM : 1}
                  >
                    <rect
                      x={x}
                      y={y}
                      width={STEP_W}
                      height={STEP_H}
                      rx={8}
                      fill={fill}
                      stroke={isSelected ? EDGE_COLOR : "var(--color-text)"}
                      strokeWidth={nodeStrokeWidth}
                      strokeOpacity={nodeStrokeOpacity}
                      filter={isSelected ? `url(#${selectedNodeShadowId})` : undefined}
                    />
                    {/* The second visitor's half of a shared cell, clipped to the rounded box so
                        the corners stay round. The split leans top-left to bottom-right, which
                        keeps it from reading as a border between two separate boxes. */}
                    {compareSplitSide && (
                      <>
                        <clipPath id={compareClipId}>
                          <rect x={x} y={y} width={STEP_W} height={STEP_H} rx={8} />
                        </clipPath>
                        <polygon
                          points={[
                            [x + STEP_W / 2 - COMPARE_NODE_SPLIT_SLANT, y],
                            [x + STEP_W, y],
                            [x + STEP_W, y + STEP_H],
                            [x + STEP_W / 2 + COMPARE_NODE_SPLIT_SLANT, y + STEP_H],
                          ]
                            .map(([px, py]) => `${px},${py}`)
                            .join(" ")}
                          fill={compareSideFill(compareSplitSide)}
                          clipPath={`url(#${compareClipId})`}
                          pointerEvents="none"
                        />
                        {/* The half drawn above covers the box's own outline, so the border is
                            restated on top of it — otherwise a selected shared box would look
                            bold on one side only. */}
                        <rect
                          x={x}
                          y={y}
                          width={STEP_W}
                          height={STEP_H}
                          rx={8}
                          fill="none"
                          stroke="var(--color-text)"
                          strokeWidth={nodeStrokeWidth}
                          strokeOpacity={nodeStrokeOpacity}
                          pointerEvents="none"
                        />
                      </>
                    )}
                    {/* Hover darkens the box without touching its fill: a flat wash in the text
                        colour reads as a shade over every mode's palette — plain, heat, and both
                        halves of a compared box — and inverts to a lift in dark mode, where the
                        text colour is the light one. The wash, not the border, is what carries the
                        cue: stroke width is in graph units and thins away as the map zooms out,
                        while a fill covers the same share of the box at every zoom. */}
                    {isHovered && (
                      <rect
                        x={x}
                        y={y}
                        width={STEP_W}
                        height={STEP_H}
                        rx={8}
                        fill="var(--color-text)"
                        fillOpacity={0.1}
                        pointerEvents="none"
                      />
                    )}
                    {searchPulse?.nodeId === n.id && (
                      <rect
                        x={x - 2}
                        y={y - 2}
                        width={STEP_W + 4}
                        height={STEP_H + 4}
                        rx={10}
                        fill="#f59e0b"
                        fillOpacity={0.16}
                        stroke="#ea580c"
                        strokeOpacity={0.95}
                        strokeWidth={3}
                        vectorEffect="non-scaling-stroke"
                        pointerEvents="none"
                      />
                    )}
                    <text
                      x={pos.x}
                      y={pos.y - 6}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fill={label.title}
                      fontSize={12}
                      fontWeight={500}
                    >
                      {truncate(n.name, 32)}
                    </text>
                    {nodeCompare ? (
                      // Pooled readings belong to neither run, so comparing replaces them with one
                      // per side — the box's answer to "who spent what here". Each reading sits at
                      // the foot of its own half, so which half it belongs to needs no legend, and
                      // both carry their visit count: on a shared box "1x vs 3x" is the finding,
                      // and it can only be read if the side that went once says so too.
                      nodeCompareSides.map((side, sideIndex) => (
                        <text
                          key={side}
                          x={
                            compareSplitSide && sideIndex === 0
                              ? x + STEP_W / 2 - 8
                              : x + STEP_W - 12
                          }
                          y={pos.y + 12}
                          textAnchor="end"
                          dominantBaseline="middle"
                          fill={label.sub}
                          fontSize={10}
                        >
                          {formatCompareCount(nodeCompare[side].count)}x{" "}
                          {formatDuration(nodeCompare[side].seconds)}
                        </text>
                      ))
                    ) : nodeReading !== null ? (
                      <text
                        x={x + STEP_W - 12}
                        y={pos.y + 12}
                        textAnchor="end"
                        dominantBaseline="middle"
                        fill={label.sub}
                        fontSize={10}
                      >
                        {nodeReading}
                      </text>
                    ) : null}
                    <StepAppMarks
                      apps={apps}
                      x={x + 12}
                      y={pos.y + 12}
                      color={label.sub}
                      onEnter={showAppTip}
                      onLeave={hideAppTip}
                    />
                    {stepPositionTooltip?.nodeId === n.id &&
                      (() => {
                        // Read the cue that is running (if any) and take the side of the cell its
                        // route keeps clearest, so the label never lands on the travelling dots.
                        const cuedRoute = transitionPulse
                          ? edgeRoutePoints(layout, transitionPulse.from, transitionPulse.to)
                          : null;
                        const side = stepPositionTipSide(pos, cuedRoute);
                        const tipY =
                          side === "top"
                            ? y - STEP_POSITION_TIP_GAP - STEP_POSITION_TIP_H
                            : y + STEP_H + STEP_POSITION_TIP_GAP;
                        return (
                          <g role="status" pointerEvents="none">
                            <rect
                              x={pos.x - STEP_POSITION_TIP_W / 2}
                              y={tipY}
                              width={STEP_POSITION_TIP_W}
                              height={STEP_POSITION_TIP_H}
                              rx={6}
                              fill="#000"
                            />
                            <text
                              x={pos.x}
                              y={tipY + STEP_POSITION_TIP_H / 2}
                              textAnchor="middle"
                              dominantBaseline="middle"
                              fill="#fff"
                              fontSize={12}
                              fontWeight={500}
                            >
                              {stepPositionTooltip.label}
                            </text>
                          </g>
                        );
                      })()}
                  </g>
                );
              })}

              {/* Paint arrowheads after the cards so each one stays fully visible where it meets
                  the step border instead of being clipped by the node fill. The apex sits on the
                  border and the body points inwards along the route's last segment. End
                  transitions use the hollow terminal itself as their endpoint. */}
              {edges.map(e => {
                if (e.to === graph.end) return null;
                const key = edgeKey(e.from, e.to);
                const geometry = edgeGeometry.get(key);
                if (!geometry?.arrowheadD) return null;
                const { routePoints } = geometry;
                const isFocusedEndpoint = focusPath?.edgeKeys.has(key) ?? false;
                const isInstanceEndpoint = highlightedInstancePath?.edgeKeys.has(key) ?? false;
                const isEmphasizedEndpoint =
                  (selectedTransition?.from === e.from && selectedTransition.to === e.to) ||
                  activeEdgeKey === key;
                const isPulseEndpoint =
                  transitionPulse?.from === e.from && transitionPulse.to === e.to;
                const compareEndpoint = comparePaths
                  ? {
                      a: comparePaths.a.edgeKeys.has(key),
                      b: comparePaths.b.edgeKeys.has(key),
                    }
                  : null;
                const compareEndpointSide =
                  compareEndpoint && compareEndpoint.a !== compareEndpoint.b
                    ? compareEndpoint.a
                      ? "a"
                      : "b"
                    : null;
                const isSharedCompareEndpoint =
                  compareEndpoint !== null && compareEndpoint.a && compareEndpoint.b;
                // The head is thinner than the emphasized stroke it sits on, so at normal size it
                // vanishes into a highlighted edge. Grow it (and take the edge's own colour) so
                // the arrival point stays readable.
                return (
                  <path
                    key={`edge-endpoint:${key}`}
                    data-edge-endpoint={key}
                    d={
                      isFocusedEndpoint
                        ? arrowheadPath(
                            routePoints[routePoints.length - 1],
                            routePoints[routePoints.length - 2],
                            fitArrowheadLength(
                              routePoints,
                              ARROWHEAD_LENGTH * ARROWHEAD_FOCUS_SCALE,
                            ),
                          )
                        : geometry.arrowheadD
                    }
                    // Resting heads match their muted route. Interaction promotes both pieces to
                    // ink, while compare and pulse states keep the same semantic colour as their
                    // line so the arrow never looks detached from the path it terminates.
                    fill={
                      isPulseEndpoint
                        ? transitionPulse?.side
                          ? compareSideColor(transitionPulse.side)
                          : "var(--color-transition-cue)"
                        : isInstanceEndpoint && highlightedCompareColor
                          ? highlightedCompareColor
                          : compareEndpointSide
                            ? compareSideColor(compareEndpointSide)
                            : isInstanceEndpoint
                              ? "var(--color-text-mid)"
                              : isFocusedEndpoint || isEmphasizedEndpoint || isSharedCompareEndpoint
                                ? "var(--color-text)"
                                : EDGE_COLOR
                    }
                    opacity={
                      focusPath
                        ? isFocusedEndpoint
                          ? 1
                          : EMPHASIS_DIM
                        : isEmphasizing
                          ? EMPHASIS_DIM
                          : 1
                    }
                    pointerEvents="none"
                  />
                );
              })}
            </g>

            {/* Screen-space copies of selected transition endpoints that have left the viewport.
                Step copies carry data-node-id, so clicks follow the same selection and double-click
                path as their real cells. Terminal copies are informational and let clicks through. */}
            {selectedEndpointNodes.map(({ node, terminal }) => {
              const nodeReading = stepReadingText(node);
              const apps = appsByNode.get(node.id) ?? [];
              return (
                <g
                  key={`edge-preview:${node.id}`}
                  ref={element => {
                    if (element) edgePreviewRefs.current.set(node.id, element);
                    else edgePreviewRefs.current.delete(node.id);
                  }}
                  data-node-id={terminal === null ? node.id : undefined}
                  data-edge-endpoint-preview="true"
                  className={terminal === null ? "cursor-pointer" : undefined}
                  style={{ display: "none" }}
                  pointerEvents={terminal === null ? undefined : "none"}
                  role={terminal === null ? "button" : undefined}
                  aria-label={node.name}
                >
                  {terminal !== null ? (
                    <>
                      <rect
                        x={-TERMINAL_PREVIEW_W / 2}
                        y={-TERMINAL_PREVIEW_H / 2}
                        width={TERMINAL_PREVIEW_W}
                        height={TERMINAL_PREVIEW_H}
                        rx={TERMINAL_PREVIEW_H / 2}
                        fill="var(--color-surface-sunken)"
                        fillOpacity={0.98}
                        stroke="var(--color-text)"
                        strokeOpacity={0.82}
                        strokeWidth={1.5}
                        strokeDasharray="5 3"
                        strokeLinecap="round"
                        filter={`url(#${edgePreviewShadowId})`}
                      />
                      <BoundaryGlyph
                        kind={terminal}
                        x={-TERMINAL_PREVIEW_W / 2 + 18}
                        y={0}
                        radius={7}
                      />
                      <text
                        x={8}
                        y={0}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fill="var(--color-text)"
                        fontSize={12}
                        fontWeight={600}
                        pointerEvents="none"
                      >
                        {t(terminal)}
                      </text>
                    </>
                  ) : (
                    <>
                      <rect
                        x={-STEP_W / 2}
                        y={-STEP_H / 2}
                        width={STEP_W}
                        height={STEP_H}
                        rx={8}
                        fill="var(--color-surface-sunken)"
                        fillOpacity={0.98}
                        stroke="var(--color-text)"
                        strokeOpacity={0.82}
                        strokeWidth={1.5}
                        strokeDasharray="5 3"
                        strokeLinecap="round"
                        filter={`url(#${edgePreviewShadowId})`}
                      />
                      <text
                        x={0}
                        y={-6}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fill="var(--color-text)"
                        fontSize={12}
                        fontWeight={600}
                        pointerEvents="none"
                      >
                        {truncate(node.name, 32)}
                      </text>
                      {nodeReading !== null && (
                        <text
                          x={STEP_W / 2 - 12}
                          y={12}
                          textAnchor="end"
                          dominantBaseline="middle"
                          fill="var(--color-text-dim)"
                          fontSize={10}
                          pointerEvents="none"
                        >
                          {nodeReading}
                        </text>
                      )}
                      <StepAppMarks
                        apps={apps}
                        x={-STEP_W / 2 + 12}
                        y={12}
                        color="var(--color-text-dim)"
                      />
                    </>
                  )}
                </g>
              );
            })}
          </svg>

          {/* Inner shading around the canvas edge: with a plain white background the map has no
              boundary of its own, so a shadow cast inward from all four sides reads as the edge of
              the sheet and keeps content near the rim from floating off into the chrome. */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-10 shadow-[inset_0_0_22px_rgba(16,16,18,0.10),inset_0_0_3px_rgba(16,16,18,0.06)]"
          />

          {/* Map chrome: controls at the top-right and the colour key at the bottom-right. The
              wrapper lets presses through to the map; each control takes them back for itself. */}
          <div className="pointer-events-none absolute inset-0 z-20 flex flex-col items-end justify-between gap-3 p-3 text-xs">
            <div className="flex w-full flex-wrap items-start justify-between gap-2">
              <div className="flex max-w-full flex-wrap items-center gap-2">
                <ProcessGraphCaseControl state={state} />
                {!state.comparePair && (
                  <ProcessFilters state={state} peopleOptions={peopleOptions} />
                )}
              </div>
              <div
                ref={topControlsRef}
                className="pointer-events-auto ml-auto flex max-w-full flex-wrap items-center justify-end gap-2"
                aria-label={t("view_controls")}
              >
                <Tooltip.Provider delayDuration={500} skipDelayDuration={0}>
                  {searchOpen ? (
                    <div className="inline-flex h-8 items-stretch overflow-hidden rounded-md border border-outline-strong bg-surface shadow-lift">
                      <Search
                        className="ml-2.5 h-3.5 w-3.5 self-center text-text-dim"
                        aria-hidden="true"
                      />
                      <input
                        ref={searchInputRef}
                        type="search"
                        value={searchQuery}
                        onChange={event => setSearchQuery(event.currentTarget.value)}
                        onKeyDown={event => {
                          if (event.key === "Enter") {
                            event.preventDefault();
                            moveToSearchMatch(event.shiftKey ? -1 : 1);
                          } else if (event.key === "Escape") closeSearch();
                        }}
                        placeholder={t("search_steps")}
                        aria-label={t("search_steps")}
                        className="w-40 bg-surface px-2 text-text outline-none placeholder:text-text-dim"
                      />
                      {searchQuery.trim() && (
                        <span
                          className="flex min-w-12 items-center justify-center border-l border-outline px-1.5 text-text-dim tabular-nums"
                          aria-live="polite"
                        >
                          {searchMatches.length === 0
                            ? t("no_search_matches")
                            : `${searchMatchIndex < 0 ? 0 : searchMatchIndex + 1}/${searchMatches.length}`}
                        </span>
                      )}
                      <ControlTooltip label={t("previous_search_match")}>
                        <button
                          type="button"
                          disabled={searchMatches.length === 0}
                          onClick={() => moveToSearchMatch(-1)}
                          aria-label={t("previous_search_match")}
                          className="flex w-7 items-center justify-center border-l border-outline text-text-mid hover:bg-surface-strong disabled:opacity-35"
                        >
                          <ChevronUp className="h-3.5 w-3.5" aria-hidden="true" />
                        </button>
                      </ControlTooltip>
                      <ControlTooltip label={t("next_search_match")}>
                        <button
                          type="button"
                          disabled={searchMatches.length === 0}
                          onClick={() => moveToSearchMatch(1)}
                          aria-label={t("next_search_match")}
                          className="flex w-7 items-center justify-center text-text-mid hover:bg-surface-strong disabled:opacity-35"
                        >
                          <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
                        </button>
                      </ControlTooltip>
                      <ControlTooltip label={t("close_search")}>
                        <button
                          type="button"
                          onClick={closeSearch}
                          aria-label={t("close_search")}
                          className="flex w-7 items-center justify-center border-l border-outline text-text-mid hover:bg-surface-strong"
                        >
                          <X className="h-3.5 w-3.5" aria-hidden="true" />
                        </button>
                      </ControlTooltip>
                    </div>
                  ) : (
                    <ControlTooltip label={t("search_steps")}>
                      <button
                        type="button"
                        onClick={() => setSearchOpen(true)}
                        aria-label={t("search_steps")}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-outline bg-surface text-text shadow-sm outline-none hover:bg-surface-strong focus-visible:ring-2 focus-visible:ring-info/25"
                      >
                        <Search className="h-4 w-4" aria-hidden="true" />
                      </button>
                    </ControlTooltip>
                  )}
                  <div className="inline-flex h-8 items-stretch overflow-hidden rounded-md border border-outline-strong bg-surface shadow-lift">
                    <ControlTooltip label={t("zoom_out")}>
                      <button
                        type="button"
                        onClick={() => zoomByStep(1 / ZOOM_STEP)}
                        aria-label={t("zoom_out")}
                        className="flex w-8 items-center justify-center text-text-mid transition hover:bg-surface-strong hover:text-text"
                      >
                        <Minus className="h-3.5 w-3.5" aria-hidden="true" />
                      </button>
                    </ControlTooltip>
                    <ControlTooltip label={t("set_zoom_percentage")}>
                      <input
                        ref={zoomInputRef}
                        type="text"
                        inputMode="decimal"
                        defaultValue="100%"
                        aria-label={t("set_zoom_percentage")}
                        onFocus={event => event.currentTarget.select()}
                        onBlur={applyTypedZoom}
                        onKeyDown={event => {
                          if (event.key === "Enter") event.currentTarget.blur();
                          if (event.key === "Escape") {
                            event.currentTarget.value = `${Math.round(view.current.k * 100)}%`;
                            event.currentTarget.blur();
                          }
                        }}
                        className="w-14 border-x border-outline bg-surface text-center text-text tabular-nums outline-none transition hover:bg-surface-strong focus:bg-surface-strong focus:ring-2 focus:ring-inset focus:ring-info/25"
                      />
                    </ControlTooltip>
                    <ControlTooltip label={t("zoom_in")}>
                      <button
                        type="button"
                        onClick={() => zoomByStep(ZOOM_STEP)}
                        aria-label={t("zoom_in")}
                        className="flex w-8 items-center justify-center text-text-mid transition hover:bg-surface-strong hover:text-text"
                      >
                        <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                      </button>
                    </ControlTooltip>
                    <ControlTooltip label={t("fit_to_screen")}>
                      <button
                        type="button"
                        onClick={fitView}
                        aria-label={t("fit_to_screen")}
                        className="flex w-8 items-center justify-center border-l border-outline text-text-mid transition hover:bg-surface-strong hover:text-text"
                      >
                        <Scan className="h-3.5 w-3.5" aria-hidden="true" />
                      </button>
                    </ControlTooltip>
                  </div>
                </Tooltip.Provider>

                {onToggleExpanded && (
                  <Tooltip.Provider>
                    <ControlTooltip
                      label={t(expanded ? "processPage.restore" : "processPage.expand")}
                    >
                      <button
                        type="button"
                        onClick={onToggleExpanded}
                        aria-label={t(expanded ? "processPage.restore" : "processPage.expand")}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-outline bg-surface text-text shadow-sm hover:bg-surface-strong focus-visible:ring-2 focus-visible:ring-info/25"
                      >
                        {expanded ? (
                          <Minimize2 size={14} aria-hidden="true" />
                        ) : (
                          <Maximize2 size={14} aria-hidden="true" />
                        )}
                      </button>
                    </ControlTooltip>
                  </Tooltip.Provider>
                )}
                <Popover.Root>
                  <Popover.Trigger
                    type="button"
                    data-tour="graph-settings"
                    aria-label={t("graph_settings")}
                    className="inline-flex h-8 w-8 self-start items-center justify-center rounded-md border border-outline bg-surface text-text shadow-sm outline-none hover:bg-surface-strong focus-visible:ring-2 focus-visible:ring-info/25"
                  >
                    <SlidersHorizontal className="h-3.5 w-3.5" aria-hidden="true" />
                  </Popover.Trigger>
                  <Popover.Portal>
                    <Popover.Content
                      align="end"
                      side="bottom"
                      sideOffset={8}
                      // Opening the panel would otherwise land focus on the first control in it —
                      // the help button — which pops its tooltip open unasked.
                      onOpenAutoFocus={e => e.preventDefault()}
                      className="pd-theme z-50 w-80 rounded-lg border border-outline bg-surface p-3 text-xs text-text shadow-xl"
                    >
                      <div className="space-y-3">
                        {/* The cells and routes are saying which run visits them and what each
                            spent there, so there is no traffic, tint or reading left to choose;
                            the settings come back on the way out of compare. */}
                        {!comparing && (
                          <>
                            <div className="flex items-center justify-between gap-3">
                              <span className="inline-flex items-center gap-1 text-text-dim">
                                {t("step_reading")}
                                <SettingHelp label={t("step_reading_help")} />
                              </span>
                              <div className="w-40 [&_.dropdown-trigger]:h-7 [&_.dropdown-trigger]:rounded-md [&_.dropdown-trigger]:px-2 [&_.dropdown-trigger]:text-xs">
                                <AppSelect<StepReading>
                                  label={t("step_reading")}
                                  value={stepReading}
                                  onChange={setStepReading}
                                  choices={[
                                    { id: "auto", display: t("step_reading_auto") },
                                    { id: "visitsAvg", display: t("step_reading_visits_avg") },
                                    { id: "totalTime", display: t("step_reading_total_time") },
                                    { id: "cases", display: t("step_reading_cases") },
                                    { id: "none", display: t("step_reading_none") },
                                  ]}
                                />
                              </div>
                            </div>
                            <div className="flex items-center justify-between gap-3">
                              <span className="text-text-dim">{t("color_by")}</span>
                              <span className="inline-flex overflow-hidden rounded-md border border-outline">
                                <button
                                  type="button"
                                  onClick={() => setColorBy("none")}
                                  className={segBtn(colorBy === "none")}
                                >
                                  {t("color_none")}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setColorBy("time")}
                                  className={`border-l border-outline ${segBtn(colorBy === "time")}`}
                                >
                                  {t("color_time")}
                                </button>
                              </span>
                            </div>
                            <div className="flex items-center justify-between gap-3">
                              <span className="inline-flex items-center gap-1 text-text-dim">
                                {t("traffic")}
                                <SettingHelp label={t("traffic_help")} />
                              </span>
                              <span className="inline-flex overflow-hidden rounded-md border border-outline">
                                <button
                                  type="button"
                                  onClick={() => setTraffic(false)}
                                  className={segBtn(!showVolume)}
                                >
                                  {t("traffic_off")}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setTraffic(true)}
                                  className={`border-l border-outline ${segBtn(showVolume)}`}
                                >
                                  {t("traffic_on")}
                                </button>
                              </span>
                            </div>
                          </>
                        )}
                        <div className="flex items-center justify-between gap-3">
                          <span className="inline-flex items-center gap-1 text-text-dim">
                            {t("edge_routing")}
                            <SettingHelp label={t("edge_routing_help")} />
                          </span>
                          <span className="inline-flex overflow-hidden rounded-md border border-outline">
                            <button
                              type="button"
                              onClick={() => setEdgeRouting("bundled")}
                              className={segBtn(edgeRouting === "bundled")}
                            >
                              {t("edge_routing_bundled")}
                            </button>
                            <button
                              type="button"
                              onClick={() => setEdgeRouting("separate")}
                              className={`border-l border-outline ${segBtn(edgeRouting === "separate")}`}
                            >
                              {t("edge_routing_separate")}
                            </button>
                          </span>
                        </div>
                        {!comparing && (
                          <div className="flex items-center justify-between gap-3">
                            <span className="inline-flex items-center gap-1 text-text-dim">
                              <label htmlFor={simplifyLimitId}>
                                {t("processPanel.simplifyAbove")}
                              </label>
                              <SettingHelp
                                label={t("processPanel.autoSimplifyHelp", {
                                  stepLimit: t("n_steps", { count: state.simpleMapLimit.steps }),
                                  transitions: state.simpleMapLimit.connections,
                                })}
                              />
                            </span>
                            <span className="inline-flex items-center gap-1.5 text-text-dim">
                              <input
                                id={simplifyLimitId}
                                type="number"
                                min={1}
                                step={1}
                                value={state.simpleMapLimit.steps}
                                onChange={event => {
                                  const value = event.currentTarget.valueAsNumber;
                                  if (!Number.isInteger(value) || value < 1) return;
                                  state.setSimpleMapStepLimit(value);
                                  state.applyScope({
                                    ...state.appliedScope,
                                    commonPaths: undefined,
                                  });
                                }}
                                className="h-7 w-16 rounded-md border border-outline bg-surface px-2 text-text tabular-nums focus-visible:ring-2 focus-visible:ring-info/25"
                              />
                              {t("processPanel.simplifyStepUnit", {
                                count: state.simpleMapLimit.steps,
                              })}
                            </span>
                          </div>
                        )}
                      </div>
                    </Popover.Content>
                  </Popover.Portal>
                </Popover.Root>
              </div>
            </div>

            {/* Only meaningful while the cells are tinted by duration, so it comes and goes with
                that lens rather than sitting there explaining a colour nothing is using. */}
            {heatOn && Number.isFinite(durMin) && durMax >= durMin && (
              <div
                ref={heatLegendRef}
                className="pointer-events-auto flex max-w-full shrink-0 items-center gap-2 rounded-md border border-outline-strong bg-surface px-2.5 py-1.5 text-[11px] text-text-dim shadow-lift"
              >
                {/* "Total", not "avg": the ramp is normalised over each step's summed time across
                    its occurrences, while the cell under it prints the average of one. */}
                <span className="font-medium text-text-mid">{t("total_duration")}</span>
                <span className="tabular-nums">{formatDuration(Math.round(durMin))}</span>
                {/* The same low-to-high blue ramp used by the map cells. */}
                <span
                  aria-hidden="true"
                  className="h-1.5 w-24 rounded-full border border-outline"
                  style={{ background: HEAT_LEGEND_GRADIENT }}
                />
                <span className="tabular-nums">{formatDuration(Math.round(durMax))}</span>
              </div>
            )}
          </div>
          {/* One overlay slot, shared by the in-progress and failed states. First layout, or one
            replacing a map of only Start and End: a centered message covers the area. Later
            layouts: the previous graph stays visible (and interactive) underneath, so a small
            corner badge signals the recompute — or, on a fault, why what's on screen is now
            stale. */}
          {!state.simplified &&
            selectedInstanceIds.length === 0 &&
            !layoutPending &&
            !layoutFailed && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-canvas px-6 text-center text-sm text-text-mid">
                {t("processPage.noRunsForFilters")}
              </div>
            )}
          {state.simplified &&
            selectedInstanceIds.length === 0 &&
            !layoutPending &&
            !layoutFailed && (
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-canvas px-6 text-center text-sm text-text-mid">
                <p>{t("processPage.noCommonPathsHelp")}</p>
                <button
                  type="button"
                  onClick={() => state.applyScope({ ...state.appliedScope, commonPaths: false })}
                  className="rounded-lg border border-outline bg-surface px-3 py-2 text-text hover:bg-surface-strong focus-visible:ring-2 focus-visible:ring-info/20"
                >
                  {t("processPage.turnOffCommonPaths")}
                </button>
              </div>
            )}
          {layoutFailed ? (
            showsSteps ? (
              <div className="pointer-events-none absolute left-1/2 top-3 z-10 flex max-w-[16rem] -translate-x-1/2 items-center gap-1.5 rounded-md border border-outline bg-surface/90 px-2 py-1 text-[11px] text-text-dim shadow-sm">
                <TriangleAlert className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                {t("graph_layout_failed")}
              </div>
            ) : (
              <div className="pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-canvas px-6 text-center text-sm text-text-dim">
                <TriangleAlert className="h-6 w-6" aria-hidden="true" />
                {t("graph_layout_failed")}
              </div>
            )
          ) : (
            layoutPending &&
            (showsSteps ? (
              <div className="pointer-events-none absolute left-1/2 top-3 z-10 flex -translate-x-1/2 items-center gap-1.5 rounded-md border border-outline bg-surface/90 px-2 py-1 text-[11px] text-text-dim shadow-sm">
                <LoaderCircle className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                {t("regenerating_graph")}
              </div>
            ) : (
              <div className="pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-canvas text-sm text-text-dim">
                <LoaderCircle className="h-6 w-6 animate-spin" aria-hidden="true" />
                {t("generating_graph")}
              </div>
            ))
          )}
        </div>
        {appTip && (
          <div
            className="pointer-events-none fixed z-50 whitespace-nowrap rounded-md bg-overlay px-2 py-1 text-[11px] text-on-accent shadow-md"
            style={{
              left: appTip.x,
              top: appTip.y,
              // Center above the cursor; flip below when too near the top of the viewport.
              transform:
                appTip.y < 60 ? "translate(-50%, 16px)" : "translate(-50%, calc(-100% - 12px))",
            }}
          >
            {appTip.app}
          </div>
        )}
        {/* The logo has its own name to give, and two bubbles at one cursor is noise. */}
        {nodeTip && !appTip && (
          <div
            role="tooltip"
            /* Surface-on-ink rather than a colour of its own: the hint is naming what is already
               there, so it stays quiet and lets a little of the cell show through. The tokens keep
               it white-on-black in the light theme and inverted in the dark one. */
            className="pointer-events-none fixed z-50 inline-flex h-7 items-center whitespace-nowrap rounded-full border border-outline bg-surface/85 px-2.5 text-xs font-medium text-text shadow-sm backdrop-blur-[2px]"
            style={{
              left: nodeTip.x,
              top: nodeTip.y,
              transform: nodeTip.side === "above" ? "translate(-50%, -100%)" : "translate(-50%, 0)",
            }}
          >
            {t("node_hover_hint")}
          </div>
        )}
        {boundaryTip && (
          <div
            role="tooltip"
            className="pointer-events-none fixed z-50 whitespace-nowrap rounded-md bg-overlay px-2 py-1 text-[11px] text-on-accent shadow-md"
            style={{
              left: boundaryTip.x,
              top: boundaryTip.y,
              transform:
                boundaryTip.y < 60
                  ? "translate(-50%, 16px)"
                  : "translate(-50%, calc(-100% - 12px))",
            }}
          >
            {t(boundaryTip.kind)}
          </div>
        )}
      </div>
    );
  },
);

ProcessGraphCanvas.displayName = "ProcessGraphCanvas";

export default ProcessGraphCanvas;
